import { BuildClass } from '../../../../universal';
import { React } from '../../../lib';
import { HelpTooltip } from '../help';
import { CJSX } from '../meta-types';
import { formatCompactTime, formatFullDateTime } from './date-utils';
import { getDefaultIsOwnMessage, getDefaultMessageBubbleClassName, getDefaultMessageSide, } from './message-utils';
import { getActorContextID, getContextID, getIsPrivilegedView, getMessageAuthorContextID, getMessageAuthorUserID, getMessageIsPrivileged, getMessageIsVisibleToTarget, getTargetContextDisplayName, getVisibilityText, } from './utils';
const renderDefaultMessageBlips = (input) => (React.createElement(React.Fragment, null,
    React.createElement(HelpTooltip, { title: formatFullDateTime(input.hookInput.message.CreatedAt) },
        React.createElement("span", { title: formatFullDateTime(input.hookInput.message.CreatedAt) }, formatCompactTime(input.hookInput.message.CreatedAt))),
    React.createElement(CJSX, { cond: input.hookInput.isPrivilegedView },
        React.createElement("span", { className: BuildClass([
                'rounded border px-1.5 py-0.5',
                'border-[oklch(var(--chat-l-text)_0_0_/_0.15)]',
                'bg-[oklch(var(--chat-l-surface)_0_0_/_0.8)]',
            ]) }, input.hookInput.isVisibleToTarget
            ? input.visibilityText.badgeVisibleToTarget
            : input.visibilityText.badgeHiddenFromTarget))));
export const ChatMessage = (props) => {
    const rs = props.rs;
    const message = props.message;
    const authorUserID = getMessageAuthorUserID(rs.props, message);
    const authorContextID = getMessageAuthorContextID(rs.props, message);
    const isVisibleToTarget = getMessageIsVisibleToTarget(rs.props, message);
    const isPrivilegedMessage = getMessageIsPrivileged(rs.props, message);
    const isPrivilegedView = getIsPrivilegedView(rs.props);
    const actorContextID = getActorContextID(rs.props);
    const targetContextID = getContextID(rs.props);
    const targetContextDisplayName = getTargetContextDisplayName(rs.props);
    const visibilityText = getVisibilityText(rs.props);
    const isOwnMessage = getDefaultIsOwnMessage(rs.props.userID, actorContextID, authorUserID, authorContextID);
    const hookInput = {
        message,
        currentUserID: rs.props.userID,
        actorContextID,
        targetContextID,
        targetContextDisplayName,
        isPrivilegedView,
        isVisibleToTarget,
        isPrivilegedMessage,
        isOwnMessage,
    };
    const messageSide = rs.props.resolveMessageSide?.(hookInput) ?? getDefaultMessageSide(hookInput);
    const messageBlockClass = rs.props.resolveMessageBubbleClassName?.(hookInput) ??
        getDefaultMessageBubbleClassName({ messageSide });
    const messageBlips = rs.props.renderMessageBlips == null
        ? renderDefaultMessageBlips({ hookInput, visibilityText })
        : rs.props.renderMessageBlips(hookInput);
    const ownPrivilegedToneTintClass = BuildClass([
        '!bg-[oklch(var(--chat-l-accent-bubble)_var(--chat-c-accent)_var(--chat-hue))]',
        '!text-[oklch(1_0_0)]',
    ]);
    const otherPrivilegedToneTintClass = BuildClass([
        '!bg-[oklch(var(--chat-l-privileged-bubble-other)_var(--chat-c-privileged-bubble-other)_var(--chat-hue))]',
        '!text-[oklch(var(--chat-l-privileged-text-other)_var(--chat-c-privileged-text-other)_var(--chat-hue))]',
        '!border-[oklch(var(--chat-l-privileged-border-other)_var(--chat-c-privileged-border-other)_var(--chat-hue)_/_0.82)]',
    ]);
    const privilegedToneTint = hookInput.isPrivilegedView && hookInput.isPrivilegedMessage
        ? isOwnMessage
            ? ownPrivilegedToneTintClass
            : otherPrivilegedToneTintClass
        : '';
    const restrictedPrivilegedBorderClass = hookInput.isPrivilegedView && hookInput.isPrivilegedMessage && !isVisibleToTarget
        ? '!border-2 !border-double !border-[oklch(var(--chat-l-privileged-border-restricted)_var(--chat-c-privileged-border-restricted)_var(--chat-h-privileged-border-restricted))]'
        : '';
    const hasMessageText = (message.Text?.trim() ?? '') !== '';
    return (React.createElement("div", { "data-chat-message": "true", className: BuildClass({
            'flex w-full': true,
            'justify-end': messageSide === 'right',
            'justify-start': messageSide === 'left',
        }) },
        React.createElement("div", { className: "group flex min-w-0 max-w-[78%] flex-col gap-1" },
            React.createElement("div", { className: "relative" },
                React.createElement("div", { className: BuildClass([
                        messageBlockClass,
                        privilegedToneTint,
                        restrictedPrivilegedBorderClass,
                    ]) },
                    React.createElement("div", { className: "whitespace-pre-wrap break-words select-text" }, message.Text ?? ''))),
            React.createElement(CJSX, { cond: messageBlips != null || hasMessageText },
                React.createElement("div", { className: BuildClass({
                        'mt-0.5 flex items-center gap-2 text-xs': true,
                        'justify-end': messageSide === 'right',
                        'justify-start': messageSide === 'left',
                        'text-[oklch(var(--chat-l-privileged-meta-restricted)_var(--chat-c-privileged-meta-restricted)_var(--chat-h-privileged-meta-restricted))]': hookInput.isPrivilegedView && !isVisibleToTarget,
                        'text-[oklch(var(--chat-l-privileged-meta-visible)_var(--chat-c-privileged-meta-visible)_var(--chat-hue))]': hookInput.isPrivilegedView && isVisibleToTarget,
                        'text-[oklch(var(--chat-l-text-muted)_0_0)]': !hookInput.isPrivilegedView,
                    }) },
                    messageBlips,
                    React.createElement(CJSX, { cond: hasMessageText },
                        React.createElement("button", { type: "button", onClick: () => {
                                props.onCopyMessage(message);
                            }, className: BuildClass([
                                'cursor-pointer rounded p-0.5 opacity-0 transition-all',
                                'group-hover:opacity-100 group-focus-within:opacity-100',
                                'focus:opacity-100',
                                'text-[oklch(var(--chat-l-text-faint)_0_0)]',
                                'hover:text-[oklch(var(--chat-l-text-muted)_0_0)]',
                            ]), title: "Copy message" },
                            React.createElement("img", { alt: "Copy message", src: rs.state.copiedMessageID === message.ID
                                    ? '/static/img/i8/ios16-tick.svg'
                                    : '/static/img/i8/material-outline-copy.svg', className: "h-3.5 w-3.5" }))))))));
};
//# sourceMappingURL=chat-message.js.map